import { useCallback, useMemo } from 'react';
import { getItemKey, itemSelectionToStringSet } from "./itemUtils.js";
/**
 * Spectrum collection components treat keys as strings if the `key` prop is
 * explicitly set on `Item` elements. Since we do this in `useRenderNormalizedItem`,
 * we need to ensure that keys are strings in order for selection to work. We
 * then need to convert back to the original key types in the onChange handler.
 * This hook encapsulates converting to and from strings so that keys can match
 * the original key type.
 * @param normalizedItems The normalized items to select from.
 * @param selectedKeys The currently selected keys in the collection.
 * @param defaultSelectedKeys The initial selected keys in the collection.
 * @param disabledKeys The currently disabled keys in the collection.
 * @param onChange Handler that is called when the selection changes.
 * @returns UseStringifiedMultiSelectionResult with stringified key sets and
 * string key selection change handler.
 */
export function useStringifiedMultiSelection(_ref) {
  var normalizedItems = _ref.normalizedItems,
    defaultSelectedKeys = _ref.defaultSelectedKeys,
    disabledKeys = _ref.disabledKeys,
    selectedKeys = _ref.selectedKeys,
    onChange = _ref.onChange;
  var selectedStringKeys = useMemo(() => itemSelectionToStringSet(selectedKeys), [selectedKeys]);
  var defaultSelectedStringKeys = useMemo(() => itemSelectionToStringSet(defaultSelectedKeys), [defaultSelectedKeys]);
  var disabledStringKeys = useMemo(() => itemSelectionToStringSet(disabledKeys), [disabledKeys]);
  var onStringSelectionChange = useCallback(keys => {
    if (onChange == null) {
      return;
    }
    if (keys === 'all') {
      onChange('all');
      return;
    }
    var actualKeys = new Set();
    var foundStringKeys = new Set();
    normalizedItems.forEach(item => {
      var stringKey = String(getItemKey(item));
      if (keys.has(stringKey)) {
        actualKeys.add(getItemKey(item));
        foundStringKeys.add(stringKey);
      }
    });

    // Preserve keys not found in normalizedItems (i.e., filtered out by server-side search).
    // Pass them through as-is since they are already valid ItemKey values.
    keys.forEach(key => {
      if (!foundStringKeys.has(String(key))) {
        actualKeys.add(key);
      }
    });
    onChange(actualKeys);
  }, [normalizedItems, onChange]);
  return {
    selectedStringKeys,
    defaultSelectedStringKeys,
    disabledStringKeys,
    onStringSelectionChange
  };
}
export default useStringifiedMultiSelection;
//# sourceMappingURL=useStringifiedMultiSelection.js.map